import { Component, TemplateFunction } from '@ribajs/core'
import {
  ShopifyCartService,
  ShopifyProductService,
  ToasterService,
} from '../services/index.js'

import type {
  ShopifyProduct,
  ShopifyProductVariant,
  LineItemProperties,
  ShopifySellingPlan,
} from '../types/index.js'
interface Scope {
  // OPTIONS
  product?: ShopifyProduct
  variant?: ShopifyProductVariant
  quantity: number
  sellingPlan?: ShopifySellingPlan
  /** Current (main or sub) collection */
  collectionHandle?: string
  /** Defined if there is a parent / main collection, in this case `collectionHandle` is the sub collection */
  parentCollectionHandle?: string
  /** Set this to `true` if the `sb-card-product` is a product recommendation (used for tracking) */
  isRecommendation: boolean
  /** Set this to `true` if the `sb-card-product` is a customer favorite product */
  isFavorite: boolean
  /** Set this to `true` if the `sb-card-product` is a free present for the customer itself */
  isFreePresent: boolean
  /** If true a add-to-cart loading animation is shown */
  pending: boolean
  /** If true a add-to-cart process was success */
  success: boolean
  /** If true a add-to-cart process was failed */
  failed: boolean

  // METHODS
  addToCart: SbAddToCartComponent['addToCart']

  // TEMPLATES
}

export class SbAddToCartComponent extends Component {
  public static tagName = 'sb-add-to-cart'

  protected toasterService = ToasterService.getSingleton()

  lifecycleOptions = {
    ignore: true,
  }

  public scope: Scope = {
    // OPTIONS
    product: undefined,
    variant: undefined,
    quantity: 1,
    collectionHandle: undefined,
    isRecommendation: false,
    isFavorite: false,
    isFreePresent: false,
    pending: false,
    success: false,
    failed: false,
    sellingPlan: undefined,

    // METHODS
    addToCart: this.addToCart,

    // TEMPLATES
  }

  static get observedAttributes(): string[] {
    return [
      'product',
      'variant',
      'quantity',
      'collection-handle',
      'is-recommendation',
      'is-favorite',
      'selling-plan',
    ]
  }

  protected requiredAttributes(): string[] {
    return ['product']
  }

  protected connectedCallback() {
    super.connectedCallback()
    this.init(SbAddToCartComponent.observedAttributes)
    this.addEventListeners()
  }

  protected disconnectedCallback() {
    super.disconnectedCallback()
    this.removeEventListeners()
  }

  /**
   * Additional line item cart properties, used for tracking
   * @returns
   */
  protected getProperties() {
    const cartProperties: LineItemProperties = {}
    cartProperties._isRecommendation = this.scope.isRecommendation || false
    cartProperties._isFavorite = this.scope.isFavorite || false
    cartProperties._isFreePresent = this.scope.isFreePresent || false

    return cartProperties
  }

  /**
   * Add the selected variant (selected by the selected options) to the cart
   * @returns
   */
  public async addToCart(event?: Event) {
    if (!this.scope.variant || !this.scope.product) {
      return
    }
    event?.preventDefault()

    // Validate the quantity before adding to cart
    const validateQuantityResult = ShopifyCartService.validateQuantity(
      this.scope.variant,
      this.scope.quantity,
    )

    // Only proceed if we have a valid quantity
    if (!validateQuantityResult.isValid) {
      await ShopifyProductService.showQuantityValidationError(
        validateQuantityResult,
      )
      return
    }

    try {
      // We do not use the event listener here, because we want to show the loading state only for this component instance
      this.onCartRequestStart()
      await ShopifyCartService.add(
        this.scope.product,
        this.scope.variant.id,
        {
          quantity: validateQuantityResult.allowedUserInput,
          sellingPlanId: this.scope.sellingPlan?.id,
        },
        this.getProperties(),
      )
    } catch (error) {
      this.onCartRequestFailed()
      console.error('addToCart error', error)
    }
  }

  protected onCartRequestStart() {
    this.scope.pending = true
  }

  protected onCartRequestComplete() {
    if (this.scope.pending) {
      this.scope.pending = false
      this.scope.success = !this.scope.failed
      setTimeout(() => {
        this.scope.success = false
        this.scope.failed = false
      }, 2000)
    }
  }

  protected onCartRequestFailed() {
    this.scope.pending = false
    this.scope.success = false
    this.scope.failed = true
    setTimeout(() => {
      this.scope.success = false
      this.scope.failed = false
    }, 2000)
  }

  async afterBind() {
    await super.afterBind()
    this.classList.add(`${SbAddToCartComponent.tagName}-ready`)
  }

  protected addEventListeners() {
    ShopifyCartService.events.on(
      'ShopifyCart:request:complete',
      this.onCartRequestComplete,
      this,
    )
    ShopifyCartService.events.on(
      'ShopifyCart:request:failed',
      this.onCartRequestFailed,
      this,
    )
  }

  protected removeEventListeners() {
    ShopifyCartService.events.off(
      'ShopifyCart:request:complete',
      this.onCartRequestComplete,
      this,
    )
    ShopifyCartService.events.off(
      'ShopifyCart:request:failed',
      this.onCartRequestFailed,
      this,
    )
  }

  protected template(): ReturnType<TemplateFunction> {
    return null
  }
}
